import { notifyError, notifySuccess } from "@/toast-notifications";
import { deleteCustomer, deleteCustomers, getCustomer, getCustomerReservations, getCustomersById, getCustomersFile, getCustomersIds, getDuplicatedCustomers, getSearchCustomers, patchCustomer, postBulkFuseCustomers, postCustomer, postDownloadCustomersFile } from "./requests";
import { RESERVATIONS_KEY } from "../reservations/hooks";
import { useQuery, useMutation } from "react-query";
import queryClient from "../queryClient";
import { removeStoreCustomerFromReservations, updateCacheDetailCustomer, updateCacheDetailCustomers, updateStoreCustomerReservations } from "./utils";

export const CUSTOMERS_KEY = "customers";

export const useGetCustomers = ({ value="", fields = [], limit, offset = 0, ordering = [], }, options = {}) => 
    useQuery(
        [CUSTOMERS_KEY, 'list', { value, fields, limit, offset, ordering }],
        () => getSearchCustomers(value, fields, limit, offset, ordering),
        {
            ...options,
            onSuccess: (data) => {
                const payload = Array.isArray(data) ? data: data.results;
                updateCacheDetailCustomers(payload);
            },
            onError: () => {
                notifyError("Une erreur est survenue lors de la récupération des clients.");
            }
        }
    );

export const useGetCustomer = (id, options = {}) => 
    useQuery(
        [CUSTOMERS_KEY, 'detail', id],
        () => getCustomer(id),
        {
            ...options,
            onError: () => {
                notifyError("Une erreur est survenue lors de la récupération du client.");
            }
        }
    );

export const useGetCustomersById = ({ ids = [], accept_adds = undefined }, options = {}) => 
    useQuery(
        [CUSTOMERS_KEY, 'list', ids, accept_adds],
        () => getCustomersById(ids, accept_adds),
        {
            ...options,
            enabled: ids.length > 0,
            onSuccess: customers => {
                updateCacheDetailCustomers(customers);
            },
            onError: () => {
                notifyError("Une erreur est survenue lors de la récupération des clients.");
            }
        }
    );


const CUSTOMERS_IDS_KEY = "customers-ids";

export const useGetCustomersIds = ({ accept_adds, search, fields, ordering, }, options = {}) => 
    useQuery(
        [CUSTOMERS_IDS_KEY, 'list', { accept_adds, search, fields, ordering, }],
        () => getCustomersIds(accept_adds, search, fields, ordering),
        {
            ...options,
            onError: () => {
                notifyError("Une erreur est survenue lors de la récupération des ids clients.");
            }
        }
    );

export const useGetCustomerReservations = (id, limit = undefined, offset = undefined, ordering = [], options = {}) => 
    useQuery(
        [RESERVATIONS_KEY, 'list', { customer: id, limit, offset, ordering, }],
        () => getCustomerReservations(id, { limit, offset, ordering, }),
        {
            ...options,
            onError: () => {
                notifyError("Une erreur est survenue lors de la récupération des réservations du client.");
            }
        }
    );


export const DUPLICATED_CUSTOMERS_KEY = "duplicated-customers";

export const useGetDuplicatedCustomers = (field, options = {}) => 
    useQuery(
        [DUPLICATED_CUSTOMERS_KEY, field],
        () => getDuplicatedCustomers(field),
        {
            ...options,
            onError: () => {
                notifyError("Une erreur est survenue lors de la récupération des client dupliqués.");
            }
        }
    );

export const useDownloadCustomerFile = (options = {}) =>
    useMutation(
        postDownloadCustomersFile,
        {
            ...options,
            onError: () => {
                notifyError("Une erreur est survenue lors du téléchargement du fichier client.");
            },
        }
    )

export const usePostCustomer = (options = {}) => 
    useMutation(
        postCustomer,
        {
            ...options,
            onSuccess: (newCustomer) => {
                updateCacheDetailCustomer(newCustomer);
                queryClient.invalidateQueries([CUSTOMERS_KEY, 'list']);
                notifySuccess("Le client ont été ajouté avec succès.");
            },
            onError: () => {
                notifyError("Une erreur est survenue lors de l'ajout du client.");
            },
        }
    );

export const usePatchCustomer = (options = {}) => 
    useMutation(
        patchCustomer,
        {
            ...options,
            onSuccess: (newCustomer) => {
                updateCacheDetailCustomer(newCustomer);
                updateStoreCustomerReservations(newCustomer);
                queryClient.invalidateQueries([CUSTOMERS_KEY, 'list']);
                notifySuccess("Le client ont été modifié avec succès.");
            },
            onError: () => {
                notifyError("Une erreur est survenue lors de la mise à jour du client.");
            },
        }
    );

export const useDeleteCustomer = (options = {}) => 
    useMutation(
        deleteCustomer,
        {
            ...options,
            onSuccess: (_, customerId) => {
                queryClient.removeQueries([CUSTOMERS_KEY, 'detail', customerId]);
                queryClient.invalidateQueries([CUSTOMERS_KEY, 'list']);
                removeStoreCustomerFromReservations(customerId);
                notifySuccess("Le client a été supprimé avec succès.");
            },
            onError: () => {
                notifyError("Une erreur est survenue lors de l'ajout du client.");
            },
        }
    );

export const useBulkDeleteCustomers = (options = {}) =>
    useMutation(
        deleteCustomers,
        {
            ...options,
            onSuccess: (_, { ids }) => {
                ids.forEach(removeStoreCustomerFromReservations);
                queryClient.invalidateQueries([CUSTOMERS_KEY]);
                queryClient.invalidateQueries([CUSTOMERS_IDS_KEY]);
                queryClient.invalidateQueries([DUPLICATED_CUSTOMERS_KEY]);
                notifySuccess("Les clients ont été supprimés avec succès.");
            },
            onError: () => {
                notifyError("Une erreur est survenue lors de la suppression des clients.");
            }
        }
    );

export const useBulkFuseCustomers = (options = {}) =>
    useMutation(
        postBulkFuseCustomers,
        {
            ...options,
            onSuccess: () => {
                queryClient.invalidateQueries([CUSTOMERS_KEY]);
                queryClient.invalidateQueries([CUSTOMERS_IDS_KEY]);
                queryClient.invalidateQueries([DUPLICATED_CUSTOMERS_KEY]);
                notifySuccess("Les clients ont été fusionnés avec succès.");
            },
            onError: () => {
                notifyError("Une erreur est survenue lors de la fusion des clients.");
            }
        }
    );


// WEBPACK FOOTER //
// ./src/queries/customers/hooks.js